import Widget from "./Widget";
import PieChartOutlineIcon from "@src/lib/icons/18px/pie-chart-outline";
import { ComplianceType } from "@src/types.generated";
import { useMemo } from "react";
import { Pie, PieChart, Label } from "recharts";
import { Skeleton } from "@src/lib/ui/Skeleton";
import { useComplianceSummaryQuery } from "@src/features/reporting/compliance/PremiumModuleDetailCertificatesTab.generated";
import { isInFuture } from "@src/features/reporting/lib/utils";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@src/lib/ui/Chart";

const chartConfig = {
  compliant: {
    label: "Compliant",
    color: "#40D986",
  },
  notCompliant: {
    label: "Not Compliant",
    color: "#9AF4C3",
  },
};

const SHPProgressWidget: React.FC = () => {
  const { data, loading } = useComplianceSummaryQuery({
    variables: {
      input: {
        complianceType: { value: ComplianceType.SexualHarassmentPrevention },
      },
    },
  });

  const complianceRecords = useMemo(
    () => data?.ComplianceRecords?.objects || [],
    [data?.ComplianceRecords?.objects],
  );

  const compliantCount = useMemo(
    () =>
      complianceRecords.filter(
        (x) => isInFuture(x.compliantUntil) && x.compliantUntil,
      )?.length || 0,
    [complianceRecords],
  );

  const notCompliantCount = useMemo(
    () => complianceRecords.length - compliantCount,
    [complianceRecords, compliantCount],
  );

  const chartData = useMemo(() => {
    return [
      {
        status: "compliant",
        label: "Compliant",
        fill: chartConfig.compliant.color,
        count: compliantCount,
      },
      {
        status: "notCompliant",
        label: "Not Compliant",
        fill: chartConfig.notCompliant.color,
        count: notCompliantCount,
      },
    ];
  }, [compliantCount, notCompliantCount]);
  if (compliantCount === 0 && notCompliantCount === 0) {
    return null;
  }
  return (
    <Widget
      title="Sexual Harassment Prevention Progress"
      Icon={PieChartOutlineIcon}
      href={{
        pathname: "/sexual-harassment-prevention",
        query: { tab: "assignments" },
      }}
    >
      <div className="flex h-full items-center justify-center">
        {loading && <Skeleton className="mx-auto size-[192px] rounded-full" />}
        {!loading && (
          <ChartContainer
            config={chartConfig}
            className="relative mx-auto aspect-square h-[192px]"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent nameKey="label" hideIndicator />}
                isAnimationActive={false}
              />
              <Pie
                isAnimationActive={false}
                data={chartData}
                dataKey="count"
                nameKey="label"
                innerRadius={75}
                outerRadius={"100%"}
                className="cursor-pointer"
              >
                <Label
                  className="cursor-pointer"
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="text-2xl-v4 font-bold-v4 tracking-m-v4"
                          >
                            {compliantCount}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="text-2xs-v4 font-normal-v4 text-muted-foreground"
                          >
                            Compliant
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        )}
      </div>
    </Widget>
  );
};

export default SHPProgressWidget;
